import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPreview,
  CdkDropList,
  CdkDropListGroup,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import SalesOfficeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity';
import SubRouteAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route.entity';
import { firstValueFrom } from 'rxjs';
import { GeoManagerComponent } from 'app/common/components/geo-manager/geo-manager.component';
import { MarkerGroup } from 'app/common/components/geo-manager/geo-manager.interfaces';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { RouteLoadingAnimationComponent } from 'app/shared/components/route-loading-animation/route-loading-animation.component';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { SalesOfficeService } from '../../../configuration/sales-office/sales-office.service';
import { BusinessPartnerBySubRoute } from '../interfaces/busines-partner-by-sub-route.interface';
import { ExcludedStopView, resolveExcludedStops } from './excluded-stops';
import { RolloverGroup, summarizeRollovers } from './rollover-summary';
import { unplannedSummary } from './unplanned-summary';
import { SubRouteService } from '../sub-route.service';
import { apiErrorDetail } from './api-error';
import {
  affectedDateRange,
  affectedDayCodes,
  canPinToAnotherDay,
  DisplacedChange,
  PinnedChange,
  rejectedPins,
  toWeekDayPins,
} from './manual-pin';
import { mergePartialPlan } from './partial-plan';
import {
  Substitution,
  addSubstitution,
  displacedOf,
  pinnedOf,
  removeSubstitution as removeFromSet,
} from './substitution-set';
import { computePlanDashboard, PlanDashboard } from './plan-dashboard';
import {
  clientVisitDays,
  DAY_LABELS,
  groupClientsByDay,
  scopeClientsByDay,
  VisitDayGroup,
} from './visit-scope';
import {
  clientDeliveryDays,
  DAY_CODE_TO_WEEKDAY,
  isValidGeoPoint,
  resolveStart,
  toPlanMultidayRequest,
  toSortRequest,
} from './route-optimization.mapper';
import { canPlanMultiday, missingOriginMessage, originMarkerGroup } from './planning-origin';
import { WeekDay } from './route-optimization.interface';
import { ExcludedStop, PlanMultidayResponse, SortResponse } from './route-optimization.interface';
import { RouteOptimizationService } from './route-optimization.service';

/** Estado de la máquina del drawer. */
type DrawerState = 'loading' | 'ready' | 'optimizing' | 'result' | 'error';

/** Fila de cliente excluido por GPS inválido (para la tabla). */
interface ExcludedRow {
  name: string;
  lat: string;
  lon: string;
  latBad: boolean;
  lonBad: boolean;
}

/** Frecuencia de un cliente (forma mínima usada aquí). */
interface ClientFrequency {
  _id?: string;
  type?: string;
  days?: number[];
}


/** Fila de resultado `/sort`: parada optimizada cruzada con su posición original. */
interface ResultRow {
  id: string;
  sequence: number;
  name: string;
  address: string;
  etaMin: number;
  legDistanceKm: number;
  /** posición original − optimizada; >0 subió, <0 bajó, 0 sin cambio. */
  delta: number;
}

/** Fila de parada en el resultado multi-día. */
interface MultidayStopRow {
  id: string;
  sequence: number;
  name: string;
  address: string;
  arrivalTime: string;
}

/** Ruta de un camión dentro de un día. */
interface TruckRouteView {
  truckId: string;
  label: string;
  color: string;
  stops: MultidayStopRow[];
}

/** Plan de un día para render (una o varias rutas/camiones). */
interface DayPlanView {
  date: string;
  dayLabel: string;
  trucks: TruckRouteView[];
}

/** Grupos de marcadores de un día (uno por camión). */
interface DayGroup {
  date: string;
  dayLabel: string;
  groups: MarkerGroup[];
}

/** Paleta estable de colores por día (hex, consumida por GeoManager). */
const DAY_COLORS: readonly string[] = [
  '#2563eb', // azul
  '#16a34a', // verde
  '#db2777', // rosa
  '#ea580c', // naranja
  '#7c3aed', // violeta
  '#0891b2', // cyan
  '#ca8a04', // ámbar
];

/**
 * Etiquetas de UI para el `reason` de las órdenes no asignadas
 * (ver `frontend-response-reference.md`). El enum puede crecer → usar fallback.
 */
const REASON_LABELS: Readonly<Record<string, string>> = {
  invalid_location: 'Coordenada inutilizable — revisar la geolocalización del cliente',
  swapped_location: 'Coordenada invertida — intercambiar latitud y longitud',
  exceeds_capacity: 'Más pesada que el mayor vehículo',
  out_of_range: 'Fuera de alcance para un día desde la base',
  shift_full: 'No hubo cupo en la jornada — probar otro día',
  no_capacity: 'Sin capacidad disponible',
  window: 'Ventana horaria incompatible',
  unreachable: 'Ubicación no ruteable — revisar coordenadas',
  exceeds_max_days: 'No entra en los días del tour',
  no_eligible_day: 'Sin día elegible en el rango',
  due_date: 'No entró antes de su fecha límite',
  other: 'No se pudo asignar',
};
const reasonLabel = (reason: string): string => REASON_LABELS[reason] ?? 'No se pudo asignar';

/**
 * Qué significa cada motivo y **qué puede hacer el usuario** al respecto.
 *
 * La etiqueta corta cabe en la tarjeta pero no alcanza para decidir: "sin día
 * elegible" no dice que se arregla arrastrándolo, y "no ruteable" no dice que el
 * arreglo es del dato y no del plan.
 */
const REASON_HELP: Readonly<Record<string, string>> = {
  invalid_location:
    'Su coordenada está tan lejos de la base que no puede ser real, así que el motor la apartó antes de calcular. Se arregla corrigiendo la geolocalización del cliente, no el plan.',
  swapped_location:
    'Su latitud y longitud parecen intercambiadas: al invertirlas el punto cae dentro de la zona. Se arregla intercambiando los dos valores en el maestro del cliente.',
  exceeds_capacity:
    'Sola pesa más que el mayor vehículo y no se pudo dividir. Cambiar de día no lo resuelve.',
  no_eligible_day:
    'Su frecuencia no incluye ninguno de los días planificados, así que nunca compitió por un lugar. Arrastralo a un día para asignárselo a mano.',
  shift_full:
    'La jornada de ese día se llenó antes de llegar a él. Probá arrastrarlo a un día con menos visitas.',
  out_of_range:
    'Está tan lejos de la base que ni un viaje dedicado a él solo entra en la jornada. Cambiar de día no lo resuelve.',
  unreachable:
    'Su coordenada no cae cerca de ninguna calle que el motor pueda rutear. Se arregla corrigiendo el GPS del cliente, no el plan.',
  window:
    'Su ventana horaria no entra en la franja del recorrido. Es una restricción de hora, no de día: se repite igual cualquier día.',
  no_capacity: 'No hubo capacidad disponible para atenderlo.',
  exceeds_max_days: 'No entra dentro de los días del horizonte planificado.',
  due_date: 'No se pudo ubicar antes de su fecha límite de entrega.',
  other:
    'El motor no logró ubicarlo y no informó una causa específica. Probá arrastrarlo a un día concreto.',
};

/** Grupo de clientes no planificados bajo un mismo motivo. */
/** Un cliente que quedó sin día, con lo necesario para mostrarlo como tarjeta. */
interface UnplannedClient {
  id: string;
  name: string;
  address: string;
  /** Motivo crudo del motor, para decidir si el arrastre puede servir. */
  reason: string;
}


interface UnplannedGroup {
  reason: string;
  label: string;
  items: UnplannedClient[];
}

@Component({
  selector: 'app-route-optimization-drawer',
  standalone: true,
  imports: [
    CdkDrag,
    CdkDragPreview,
    CdkDropList,
    CdkDropListGroup,
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    GeoManagerComponent,
    RouteLoadingAnimationComponent,
  ],
  templateUrl: './route-optimization-drawer.component.html',
  styles: [
    `
      /* Sin señal visible, el usuario no sabe dónde puede soltar. El CDK marca
         las listas conectadas mientras hay un arrastre en curso. */
      .cdk-drop-list-receiving {
        outline: 2px dashed rgb(99 102 241 / 0.7);
        outline-offset: 2px;
      }
      .cdk-drag-placeholder {
        opacity: 0.3;
      }
      /* La parada destino se resalta entera: hay que ver a quién se reemplaza
         antes de soltar, no después. */
      .cdk-drop-list-receiving:has(> .cdk-drag-placeholder),
      .cdk-drop-list-receiving {
        background-color: rgb(99 102 241 / 0.08);
      }
    `,
  ],
})
export class RouteOptimizationDrawerComponent implements OnInit {
  state: DrawerState = 'loading';

  subRouteName = '';
  clients: BusinessPartnerBySubRoute[] = [];
  geolocatedClients: BusinessPartnerBySubRoute[] = [];
  excludedClients: BusinessPartnerBySubRoute[] = [];
  /** Filas para la tabla de excluidos (lat/lon + cuál está mal). */
  excludedRows: ExcludedRow[] = [];
  showExcluded = false;

  // --- Fuente de clientes ---
  /** Qué conjunto se optimiza. */
  clientSource: 'assigned' | 'unassigned' | 'all' = 'assigned';

  /** Días que ya trabaja la sub-ruta (códigos 1..7), de sus frecuencias. */
  workingDayCodes: number[] = [];

  /** Días de trabajo de la sub-ruta como `WeekDay[]` (para el fallback). */
  get workingWeekDays(): WeekDay[] {
    return this.workingDayCodes.map((c) => DAY_CODE_TO_WEEKDAY[c]).filter(Boolean) as WeekDay[];
  }

  /** Etiqueta legible de los días de trabajo (ej. "Lun, Mié, Vie"). */
  get workingDaysLabel(): string {
    return this.workingDayCodes.map((c) => DAY_LABELS[c]).join(', ');
  }

  // --- Alcance (día) ---
  dayOptions: { code: number; label: string }[] = [];
  /** `0` = todos los días. */
  selectedDay = 0;
  /** Fecha de inicio del horizonte multi-día (`YYYY-MM-DD`). */
  startDate = '';

  /** Métricas del resultado (dashboard). */
  dash?: PlanDashboard;
  /** Motivo que devolvió la API en el último fallo (se muestra en pantalla). */
  errorDetail = '';

  /**
   * Sustituciones vigentes. Es **estado acumulado**, no un efecto del arrastre:
   * se mandan todas en cada replanificación. Si cada arrastre mandara solo lo
   * suyo, la anterior se perdería y las paradas saltarían de día solas.
   */
  substitutions: Substitution[] = [];
  /** Último plan multi-día vigente: la base sobre la que mezcla un recálculo acotado. */
  private lastResult?: PlanMultidayResponse;
  /** Sustituciones que todavía no se aplicaron con una replanificación. */
  pendingChanges = 0;

  /** ¿Hay cambios sin aplicar? El plan en pantalla no los contempla. */
  get hasPendingChanges(): boolean {
    return this.pendingChanges > 0;
  }

  /** Días (códigos) que la próxima replanificación va a recalcular. */
  get affectedDays(): number[] {
    const pinned: PinnedChange[] = this.substitutions.map((sub) => ({
      clientId: sub.inId,
      toDay: sub.dayCode,
      ownDays: this.visitDaysOf(sub.inId),
    }));
    const displaced: DisplacedChange[] = this.substitutions
      .filter((sub) => sub.outId)
      .map((sub) => ({
        clientId: sub.outId as string,
        fromDay: sub.dayCode,
        ownDays: this.visitDaysOf(sub.outId as string),
      }));
    return affectedDayCodes(pinned, displaced);
  }

  /** Etiquetas de los días que se van a recalcular ("jue, vie"). */
  get affectedDaysLabel(): string {
    return this.affectedDays.map((c) => DAY_LABELS[c] ?? String(c)).join(', ');
  }

  /** Etiquetas de los días que quedan como están. */
  get untouchedDaysLabel(): string {
    const affected = new Set(this.affectedDays);
    return this.multidayDays
      .map((d) => this.weekdayCode(d.date))
      .filter((c) => !affected.has(c))
      .map((c) => DAY_LABELS[c] ?? String(c))
      .join(', ');
  }

  /** Días de visita de un cliente, de sus frecuencias. */
  private visitDaysOf(clientId: string): number[] {
    const client = this.clients.find((c) => c._id === clientId);
    return client ? clientVisitDays(client) : [];
  }

  /**
   * Días elegibles **vigentes**: los de su frecuencia, corregidos por las
   * sustituciones. Es lo que decide si la corrida acotada tenía algo que decir
   * sobre este cliente.
   */
  private effectiveDaysOf(clientId: string): number[] {
    const pinned = this.substitutions.find((sub) => sub.inId === clientId);
    if (pinned) return [pinned.dayCode];
    const displaced = this.substitutions.find((sub) => sub.outId === clientId);
    const own = this.visitDaysOf(clientId);
    const base = own.length ? own : this.workingDayCodes;
    return displaced ? base.filter((d) => d !== displaced.dayCode) : base;
  }

  /**
   * La lista de no planificados no acepta drops: el arrastre es de una sola vía
   * (de sin-día hacia un día). Las paradas ya planificadas tampoco se arrastran.
   */
  readonly rejectDrop = (): boolean => false;

  /** Cliente cuyo motivo está desplegado en la lista de no planificados. */
  expandedReasonId: string | null = null;

  /** Muestra u oculta la explicación del motivo de un cliente. */
  toggleReason(clientId: string): void {
    this.expandedReasonId = this.expandedReasonId === clientId ? null : clientId;
  }

  /** Explicación accionable del motivo por el que un cliente no se pudo planificar. */
  reasonHelp(reason: string): string {
    return REASON_HELP[reason] ?? REASON_HELP['other'];
  }

  /** ¿Este cliente puede arrastrarse a un día? */
  canPin(item: { reason: string }): boolean {
    return canPinToAnotherDay(item.reason);
  }

  /** ¿Esta parada la puso el usuario a mano? */
  isManual(clientId: string): boolean {
    return this.substitutions.some((sub) => sub.inId === clientId);
  }

  /** ¿Este cliente salió del plan porque el usuario lo desplazó? */
  isDisplaced(clientId: string): boolean {
    return this.substitutions.some((sub) => sub.outId === clientId);
  }

  salesOffice?: SalesOfficeAttributes;
  hasOrigin = false;

  // --- Resultado ---
  /** Pestaña activa del resultado. El mapa se monta solo en 'map'. */
  activeTab: 'list' | 'map' = 'list';
  resultMode: 'sort' | 'multiday' = 'sort';
  result?: SortResponse;
  resultRows: ResultRow[] = [];
  multidayDays: DayPlanView[] = [];
  /** No planificadas en ningún día (definitivo), agrupadas por motivo. */
  unplannedGroups: UnplannedGroup[] = [];
  /**
   * Reprogramadas a un día posterior, **por cliente y agrupadas por día destino**.
   *
   * No es la lista cruda de `rolled_over`: esa es un evento por salto, así que una
   * orden que rueda cuatro días genera cuatro filas casi iguales. Ver
   * `rollover-summary.ts` para el porqué y los números medidos.
   */
  rolloverGroups: RolloverGroup[] = [];
  /** Se colapsa como el aviso de coordenadas: crece con el horizonte. */
  showRollovers = false;
  /**
   * El bloque de sin-día arranca colapsado, como los otros dos de la pantalla.
   *
   * A diferencia de ellos este es **accionable** —adentro vive el `cdkDropList` de
   * origen del arrastre—, así que colapsado no se puede arrastrar nada. Por eso el
   * encabezado lleva el reparto por motivo: la decisión de abrirlo se toma sin
   * abrirlo.
   */
  showUnplanned = false;

  /** Clientes reprogramados — CLIENTES, no eventos del planificador. */
  get rolloverCount(): number {
    return this.rolloverGroups.reduce((n, g) => n + g.items.length, 0);
  }

  /** El día destino con su nombre de semana, como en el resto de la pantalla. */
  rolloverDayLabel(date: string): string {
    return DAY_LABELS[this.weekdayCode(date)] ?? '';
  }

  get unplannedCount(): number {
    return this.unplannedGroups.reduce((n, g) => n + g.items.length, 0);
  }

  /** El reparto por motivo, para leerlo sin desplegar el bloque. */
  get unplannedSummaryLine(): string {
    return unplannedSummary(this.unplannedGroups);
  }
  /** Grupo de marcadores `/sort` (un solo recorrido). */
  markerGroups: MarkerGroup[] = [];
  /** Grupos por día (multi-día). El mapa renderiza solo el día seleccionado. */
  dayGroups: DayGroup[] = [];
  /** Día actualmente renderizado en el mapa (multi-día). */
  selectedMapDate = '';
  /** Marcador del origen del recorrido (si la oficina tiene punto resuelto). */
  originGroup?: MarkerGroup;

  /**
   * Marcadores a renderizar: en multi-día, SOLO el día seleccionado (evita
   * pintar cientos de pines y ralentizar el navegador); en `/sort`, el único
   * recorrido. Más el origen del recorrido si hay.
   *
   * Es un **campo estable** (no getter): un getter devolvería un array nuevo en
   * cada ciclo de detección de cambios, y el `GeoManager` re-ejecutaría
   * `fitBounds` reseteando el zoom del usuario (el "+" se regresaba).
   */
  visibleMarkerGroups: MarkerGroup[] = [];

  /** Recalcula los marcadores visibles (solo al cambiar día/resultado). */
  private refreshVisibleMarkers(): void {
    const groups: MarkerGroup[] = [];
    if (this.resultMode === 'multiday') {
      const day = this.dayGroups.find((d) => d.date === this.selectedMapDate);
      if (day) groups.push(...day.groups);
    } else {
      groups.push(...this.markerGroups);
    }
    if (this.originGroup) groups.push(this.originGroup);
    this.visibleMarkerGroups = groups;
  }

  /** Selecciona el día a renderizar en el mapa. */
  selectMapDate(date: string): void {
    this.selectedMapDate = date;
    this.refreshVisibleMarkers();
  }

  /** Camiones del día seleccionado (para la leyenda del mapa). */
  get selectedDayTrucks(): TruckRouteView[] {
    return this.multidayDays.find((d) => d.date === this.selectedMapDate)?.trucks ?? [];
  }

  /** Nº total de paradas de un día (suma de sus camiones). */
  dayStopCount(day: DayPlanView): number {
    return day.trucks.reduce((n, t) => n + t.stops.length, 0);
  }
  /** Hora de la última visita del día: dice si queda margen para sustituir. */
  dayLastArrival(day: DayPlanView): string {
    const times = day.trucks.flatMap((t) => t.stops.map((s) => s.arrivalTime)).filter(Boolean);
    return times.length ? times.sort()[times.length - 1] : '';
  }


  /**
   * Clave que fuerza recrear el `GeoManager` al cambiar de día. Es necesario
   * porque el componente solo AGREGA marcadores (no limpia los previos); al
   * recrearlo con un `containerId` nuevo se obtiene un mapa limpio.
   */
  get mapRenderKey(): string {
    return this.resultMode === 'multiday' ? this.selectedMapDate || 'none' : 'sort';
  }

  trackByKey(_index: number, key: string): string {
    return key;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public subRoute: SubRouteAttributes,
    private readonly dialogRef: MatDialogRef<RouteOptimizationDrawerComponent>,
    private readonly businessPartnerService: BusinessPartnerService,
    private readonly subRouteService: SubRouteService,
    private readonly salesOfficeService: SalesOfficeService,
    private readonly routeOptimizationService: RouteOptimizationService,
    private readonly handlerMessage: HandlerMessageService,
  ) {}

  ngOnInit(): void {
    this.subRouteName = this.subRoute?.name ?? '';
    this.startDate = new Date().toISOString().slice(0, 10);
    void this.loadSubRouteData();
  }

  get excludedCount(): number {
    return this.excludedClients.length;
  }

  /**
   * Paradas que el SERVICIO apartó por su coordenada, con el cliente resuelto.
   *
   * Distintas de `excludedClients`, que son las que el propio drawer descartó
   * antes de enviar. Para el usuario es la misma pregunta —"¿por qué falta este
   * cliente?"— detectada en otro momento, y por eso se muestran juntas.
   *
   * Sin esto una parada apartada no se renderizaba en absoluto: `resultRows` se
   * arma desde `res.order`, así que el cliente desaparecía en silencio.
   */
  serviceExcluded: ExcludedStopView[] = [];

  /** Cruza las apartadas del servicio con los clientes cargados. */
  private applyServiceExcluded(
    excluded: ExcludedStop[] | undefined,
    target: BusinessPartnerBySubRoute[],
  ): void {
    this.serviceExcluded = resolveExcludedStops(excluded, target, (reason) => ({
      label: reasonLabel(reason),
      help: REASON_HELP[reason] ?? '',
    }));
  }

  /** Texto de una coordenada para la tabla (`—` si falta). */
  private coordText(v: number | undefined | null): string {
    return v === undefined || v === null ? '—' : String(v);
  }

  /** ¿La coordenada es inválida? (falta, no finita, `0`, o fuera de rango). */
  private coordBad(v: number | undefined | null, min: number, max: number): boolean {
    return v === undefined || v === null || !Number.isFinite(v) || v === 0 || v < min || v > max;
  }

  /** ¿La fuente actual tiene frecuencias? (solo los asignados). */
  get supportsScope(): boolean {
    return this.clientSource === 'assigned';
  }

  /** Clientes que entran en el alcance del día elegido. */
  get scopedClients(): BusinessPartnerBySubRoute[] {
    // Sin asignar / Todos no tienen frecuencia en esta sub-ruta → todos.
    if (!this.supportsScope) return this.geolocatedClients;
    return scopeClientsByDay(this.geolocatedClients, this.selectedDay);
  }

  /**
   * El alcance repartido en secciones por día, para revisarlo antes de planear.
   * Solo se usa con el día en "Todos": con un día concreto la lista es plana.
   */
  get scopeGroups(): VisitDayGroup[] {
    return groupClientsByDay(this.scopedClients);
  }

  /** Días distintos cubiertos por el alcance actual (códigos 1..7). */
  get scopeDays(): number[] {
    const set = new Set<number>();
    for (const c of this.scopedClients) {
      for (const d of clientVisitDays(c)) set.add(d);
    }
    return [...set].sort((a, b) => a - b);
  }

  /**
   * Multi-día (planea/propone días):
   * - Asignados: sin día concreto y con alcance que abarca >1 día de frecuencia.
   * - Sin asignar / Todos: propone el día de cada cliente sobre los días que
   *   trabaja la sub-ruta (si trabaja >1 día).
   */
  get isMultiday(): boolean {
    if (this.supportsScope) {
      return !this.selectedDay && this.scopeDays.length > 1;
    }
    return this.workingDayCodes.length > 1;
  }

  get canOptimize(): boolean {
    return this.scopedClients.length >= 2 && this.state !== 'optimizing';
  }

  /** Cambiar el alcance invalida el resultado mostrado: vuelve al estado listo. */
  onScopeChange(): void {
    if (this.state === 'result') this.state = 'ready';
  }

  private async loadSubRouteData(): Promise<void> {
    this.state = 'loading';
    try {
      const subRouteId = this.subRoute?._id?.toString();
      const salesOfficeId = this.subRoute?.salesOffice?._id?.toString();
      if (!subRouteId) {
        this.handlerMessage.warning({ message: 'Sub-ruta inválida para optimizar.' });
        this.state = 'ready';
        return;
      }

      this.clients = await this.fetchClientsBySource(subRouteId);
      await this.loadWorkingDays(subRouteId);

      if (salesOfficeId) {
        try {
          const officeRes = await firstValueFrom(this.salesOfficeService.findOne(salesOfficeId));
          this.salesOffice = officeRes?.data;
        } catch {
          this.salesOffice = undefined;
        }
      }

      this.geolocatedClients = this.clients.filter((c) =>
        isValidGeoPoint(c?.principalAddress?.geolocationPoint),
      );
      this.excludedClients = this.clients.filter(
        (c) => !isValidGeoPoint(c?.principalAddress?.geolocationPoint),
      );
      this.excludedRows = this.excludedClients.map((c) => {
        const geo = c?.principalAddress?.geolocationPoint;
        const lat = geo?.[0];
        const lon = geo?.[1];
        return {
          name: c.commercialName || c.name || c._id,
          lat: this.coordText(lat),
          lon: this.coordText(lon),
          latBad: this.coordBad(lat, -90, 90),
          lonBad: this.coordBad(lon, -180, 180),
        };
      });

      this.buildScopeOptions();
      this.buildOriginGroup();
      this.state = 'ready';
    } catch (err: unknown) {
      this.fail(err, 'No se pudieron cargar los clientes de la sub-ruta.');
    }
  }

  /** Trae los clientes según la fuente seleccionada (asignados/sin asignar/todos). */
  private async fetchClientsBySource(subRouteId: string): Promise<BusinessPartnerBySubRoute[]> {
    const options = { currentPage: 1, limitPerPage: 1000, search: '' };
    const fetch = (assigned: boolean) =>
      firstValueFrom(
        this.businessPartnerService.findAllWithCountBySubRoute(options, subRouteId, assigned),
      ).then((res) => res?.data?.data ?? []);

    if (this.clientSource === 'assigned') return fetch(true);
    if (this.clientSource === 'unassigned') return fetch(false);

    // 'all' → asignados + sin asignar, dedup por _id.
    const [assigned, unassigned] = await Promise.all([fetch(true), fetch(false)]);
    const byId = new Map<string, BusinessPartnerBySubRoute>();
    for (const c of [...assigned, ...unassigned]) byId.set(c._id, c);
    return [...byId.values()];
  }

  /**
   * Días que trabaja la sub-ruta (de sus frecuencias `W`). Se usan como días
   * elegibles para proponer la frecuencia de los clientes sin asignar.
   */
  private async loadWorkingDays(subRouteId: string): Promise<void> {
    const codes = new Set<number>();
    try {
      const res = await firstValueFrom(this.subRouteService.findOne(subRouteId));
      const freqs = (res?.data?.frequency ?? []) as unknown as ClientFrequency[];
      for (const f of freqs) {
        if (f?.type !== 'W') continue;
        for (const d of f?.days ?? []) if (DAY_LABELS[d]) codes.add(d);
      }
    } catch {
      // Fallback: derivar de las frecuencias de los clientes cargados.
    }
    if (codes.size === 0) {
      for (const c of this.clients) {
        const freqs = (c?.frequenciesFromThisSubRoute ?? []) as unknown as ClientFrequency[];
        for (const f of freqs) {
          if (f?.type !== 'W') continue;
          for (const d of f?.days ?? []) if (DAY_LABELS[d]) codes.add(d);
        }
      }
    }
    this.workingDayCodes = [...codes].sort((a, b) => a - b);
  }

  /** Cambia la fuente de clientes y recarga. */
  onSourceChange(source: 'assigned' | 'unassigned' | 'all'): void {
    if (this.clientSource === source) return;
    this.clientSource = source;
    this.selectedDay = 0;
    this.substitutions = [];
    this.pendingChanges = 0;
    this.lastResult = undefined;
    void this.loadSubRouteData();
  }

  /** Construye el selector de día desde los clientes cargados. */
  private buildScopeOptions(): void {
    const allDays = new Set<number>();
    for (const c of this.geolocatedClients) {
      for (const d of clientVisitDays(c)) allDays.add(d);
    }
    this.dayOptions = [...allDays]
      .sort((a, b) => a - b)
      .map((code) => ({ code, label: DAY_LABELS[code] }));
  }

  /**
   * Marcador del origen del recorrido, para cualquier modelo de sub-ruta.
   *
   * No se restringe a ENTREGA: el motor arma el vehículo con `start` y `end` en
   * este punto también en preventa, así que ocultarlo dejaba al usuario mirando
   * un plan sin saber desde dónde se calculó.
   */
  private buildOriginGroup(): void {
    this.originGroup = originMarkerGroup(this.salesOffice);
  }

  /**
   * Corre el optimizador.
   *
   * @param range fechas a recalcular. Con rango, los días fuera de él conservan
   *   el plan anterior en vez de recalcularse: cada día cuesta ~1,2 s y el
   *   planificador nunca revisa un día ya resuelto, así que recalcularlos daría
   *   exactamente lo mismo.
   */
  optimize(range?: { start: string; end: string }): void {
    if (!this.canOptimize) return;
    const target = this.scopedClients;
    this.state = 'optimizing';

    if (this.isMultiday) {
      // Sin origen no se planifica: `warehouse` es obligatorio en
      // `/plan-multiday`, y anclarlo en la primera parada daría tiempos y
      // distancias que no corresponden a ninguna operación real. Mismo criterio
      // que "Rutas AI", que omite las oficinas sin geo y avisa nombrándolas.
      if (!canPlanMultiday(this.salesOffice)) {
        this.handlerMessage.warning({ message: missingOriginMessage(this.salesOffice) });
        this.state = 'ready';
        return;
      }
      const body = this.buildMultidayBody(target, range);
      this.routeOptimizationService.planMultiday(body).subscribe({
        next: (res) => this.applyMultidayResult(res, target, range),
        error: (err) => this.handleError(err),
      });
      return;
    }

    const body = toSortRequest(target, this.salesOffice);
    this.hasOrigin = !!body.start;
    this.routeOptimizationService.sort(body).subscribe({
      next: (res) => this.applySortResult(res, target),
      error: (err) => this.handleError(err),
    });
  }

  /** Los que entran, traducidos al `WeekDay` del contrato. */
  private pinnedWeekDays(): ReadonlyMap<string, WeekDay> {
    return toWeekDayPins(pinnedOf(this.substitutions));
  }

  /** Los desplazados, traducidos al `WeekDay` del contrato. */
  private displacedWeekDays(): ReadonlyMap<string, WeekDay> {
    return toWeekDayPins(displacedOf(this.substitutions));
  }

  /**
   * Sustituye una parada por un cliente sin día.
   *
   * **No llama al motor**: registra el cambio y lo refleja al instante. Cada
   * corrida cuesta ~1,2 s por día, así que dispararla por arrastre haría esperar
   * una vez por cliente para un plan que igual se recalcula al final.
   */
  onDropOnStop(event: CdkDragDrop<unknown>, day: DayPlanView, stop: MultidayStopRow): void {
    this.registerSubstitution(event, day, stop.id, stop.name);
  }

  /** Asigna a un día sin visitas: entra sin desplazar a nadie. */
  onDropOnEmptyDay(event: CdkDragDrop<unknown>, day: DayPlanView): void {
    this.registerSubstitution(event, day, null, '');
  }

  private registerSubstitution(
    event: CdkDragDrop<unknown>,
    day: DayPlanView,
    outId: string | null,
    outName: string,
  ): void {
    if (this.state === 'optimizing') return;
    if (event.previousContainer === event.container) return;
    const item = event.item.data as UnplannedClient | undefined;
    if (!item || !this.canPin(item)) return;

    const dayCode = this.weekdayCode(day.date);
    if (!dayCode) return;

    // Un cliente entra una sola vez: reemplaza su cambio anterior si lo hubiera.
    this.substitutions = addSubstitution(this.substitutions, {
      inId: item.id,
      inName: item.name,
      outId,
      outName,
      dayCode,
      date: day.date,
      dayLabel: day.dayLabel,
    });
    this.pendingChanges += 1;
    // Sale de la lista al instante: la decisión ya está tomada, aunque el motor
    // todavía no la haya validado.
    this.unplannedGroups = this.unplannedGroups
      .map((g) => ({ ...g, items: g.items.filter((i) => i.id !== item.id) }))
      .filter((g) => g.items.length > 0);
  }

  /** Deshace una sustitución: deja de estar vigente. */
  removeSubstitution(inId: string): void {
    if (this.state === 'optimizing') return;
    const before = this.substitutions.length;
    this.substitutions = removeFromSet(this.substitutions, inId);
    if (this.substitutions.length !== before) this.pendingChanges += 1;
  }

  /** Descarta todas las sustituciones vigentes. */
  clearSubstitutions(): void {
    if (this.state === 'optimizing' || !this.substitutions.length) return;
    this.substitutions = [];
    this.pendingChanges += 1;
  }

  /**
   * Replanifica aplicando todos los cambios, **acotado a los días afectados**.
   *
   * Es exacto, no una aproximación: el planificador es greedy hacia adelante
   * (nunca revisa un día ya resuelto) y el motor es determinista, así que los
   * días fuera del rango darían el mismo resultado que ya tienen.
   */
  replanPending(): void {
    if (!this.hasPendingChanges || this.state === 'optimizing') return;
    this.pendingChanges = 0;
    this.restoringDisplaced = false;
    const range = affectedDateRange(
      this.affectedDays,
      this.multidayDays.map((d) => d.date),
    );
    this.optimize(range);
  }

  /** Replanifica el horizonte completo, sin conservar ningún día. */
  replanAll(): void {
    if (this.state === 'optimizing') return;
    this.pendingChanges = 0;
    this.restoringDisplaced = false;
    this.optimize();
  }

  /**
   * Descarta las sustituciones que el motor no pudo ubicar.
   *
   * Conservar una ya demostrada imposible haría que cada corrida posterior
   * arrastre una restricción muerta y el plan empeore sin explicación. Al
   * descartarla vuelve también el desplazado, porque su salida solo tenía sentido
   * para hacerle lugar al otro.
   */
  private dropRejectedSubstitutions(): void {
    const rejected = rejectedPins(
      pinnedOf(this.substitutions),
      this.unplannedGroups.flatMap((g) => g.items.map((i) => i.id)),
    );
    if (!rejected.length) return;

    const dropped = this.substitutions.filter((sub) => rejected.includes(sub.inId));
    const names = dropped.map((sub) => sub.inName);
    this.substitutions = this.substitutions.filter((sub) => !rejected.includes(sub.inId));
    this.handlerMessage.warning({
      message:
        names.length === 1
          ? `${names[0]} no pudo asignarse al día elegido: vuelve a la lista con su motivo.`
          : `${names.length} clientes no pudieron asignarse al día elegido: vuelven a la lista con su motivo.`,
    });

    // El desplazado salió para hacerle lugar a alguien que al final no entró: sin
    // esta corrida quedaría fuera de la semana por un cambio que ya no existe.
    // Se hace una sola vez por tanda: descartada la sustitución, nada puede
    // volver a rechazarse por ella.
    const toRestore = dropped.filter((sub) => sub.outId);
    if (!toRestore.length || this.restoringDisplaced) return;
    this.restoringDisplaced = true;
    const range = affectedDateRange(
      [...new Set(toRestore.map((sub) => sub.dayCode))],
      this.multidayDays.map((d) => d.date),
    );
    this.optimize(range);
  }

  /** Evita que la reversión de un rechazo se encadene consigo misma. */
  private restoringDisplaced = false;

  /** Arma el `PlanMultidayRequest` del alcance actual. */
  private buildMultidayBody(
    target: BusinessPartnerBySubRoute[],
    range?: { start: string; end: string },
  ) {
    // Preventa es UN vendedor: siempre un recorrido por día. `toPlanMultidayRequest`
    // conserva la opción `truckCount` porque `/plan-multiday` sí admite varios y es
    // una función pura y probada, pero este drawer nunca le pasa otra cosa.
    const truckCount = 1;
    const body = toPlanMultidayRequest(target, this.salesOffice, {
      startDate: this.startDate,
      days: 7,
      // El día elegido restringe de verdad: sin esto el motor podía programar a
      // un cliente del martes en su otro día de frecuencia.
      allowedDays: this.selectedDay ? [this.selectedDay] : undefined,
      // Todas las fijaciones vigentes, no solo la última.
      pinnedDays: this.pinnedWeekDays(),
      displacedDays: this.displacedWeekDays(),
      // Se acota el horizonte, no la lista de clientes: medido, recalcular un
      // día con 40 clientes cuesta lo mismo que con los 16 elegibles.
      dateRange: range,
      fallbackDays: this.workingWeekDays,
      truckCount,
    });
    // El origen se resuelve, no se infiere: comparar el `warehouse` contra la
    // primera parada reportaba "sin origen" a una oficina legítimamente ubicada
    // sobre su primer cliente, y dependía del orden en que llegaron los datos.
    this.hasOrigin = !!resolveStart(this.salesOffice);
    return body;
  }

  private applySortResult(res: SortResponse, target: BusinessPartnerBySubRoute[]): void {
    this.applyServiceExcluded(res.excluded, target);
    this.resultMode = 'sort';
    this.activeTab = 'list';
    this.result = res;
    const originalIndexById = new Map<string, number>();
    target.forEach((c, i) => originalIndexById.set(c._id, i));
    const clientById = new Map(target.map((c) => [c._id, c]));

    this.resultRows = res.order.map((item) => {
      const client = clientById.get(item.id);
      const originalIndex = originalIndexById.get(item.id);
      const delta = originalIndex === undefined ? 0 : originalIndex - (item.sequence - 1);
      return {
        id: item.id,
        sequence: item.sequence,
        name: client?.commercialName || client?.name || item.id,
        address: client?.principalAddress?.address ?? '',
        etaMin: item.eta_min,
        legDistanceKm: item.leg_distance_km,
        delta,
      };
    });

    this.markerGroups = [
      {
        groupName: 'Ruta optimizada',
        markers: res.order
          .map((item) => {
            const geo = clientById.get(item.id)?.principalAddress?.geolocationPoint;
            if (!geo) return null;
            const client = clientById.get(item.id);
            return {
              title: `${item.sequence}. ${client?.commercialName || client?.name || item.id}`,
              subtitle: client?.principalAddress?.address,
              latitude: geo[0],
              longitude: geo[1],
              order: item.sequence,
            };
          })
          .filter((m): m is NonNullable<typeof m> => m !== null),
      },
    ];

    if (!this.hasOrigin) {
      this.handlerMessage.info({
        message: 'Origen sin coordenadas: el recorrido se ancló en la primera parada.',
      });
    }
    this.refreshVisibleMarkers();
    this.state = 'result';
  }

  private applyMultidayResult(
    fresh: PlanMultidayResponse,
    target: BusinessPartnerBySubRoute[],
    range?: { start: string; end: string },
  ): void {
    // El multi-día informa las apartadas por su propio canal (`unplanned`, con el
    // mismo motivo), así que este aviso se limpia para no arrastrar el de `/sort`.
    this.serviceExcluded = [];

    // Un recálculo acotado solo reemplaza sus días: el resto viene del plan
    // anterior, incluidos los motivos de quienes esta corrida no podía evaluar.
    const res = range
      ? mergePartialPlan(this.lastResult, fresh, {
          recalculatedDays: this.affectedDays,
          daysOf: (id) => this.effectiveDaysOf(id),
        })
      : fresh;
    this.lastResult = res;
    this.resultMode = 'multiday';
    this.activeTab = 'list';
    const clientById = new Map(target.map((c) => [c._id, c]));
    const nameOf = (id: string): string => {
      const c = clientById.get(id);
      return c?.commercialName || c?.name || id;
    };

    // Color y etiqueta estable por recorrido ("Recorrido 1" = un color en todo lado).
    const truckMeta = new Map<string, { label: string; color: string }>();
    const truckOf = (truckId: string): { label: string; color: string } => {
      let meta = truckMeta.get(truckId);
      if (!meta) {
        const i = truckMeta.size;
        meta = { label: `Recorrido ${i + 1}`, color: DAY_COLORS[i % DAY_COLORS.length] };
        truckMeta.set(truckId, meta);
      }
      return meta;
    };

    this.multidayDays = (res.days ?? [])
      .map((day) => ({
        date: day.date,
        dayLabel: DAY_LABELS[this.weekdayCode(day.date)] ?? '',
        trucks: (day.routes ?? [])
          .filter((r) => (r.stops?.length ?? 0) > 0)
          .map((r) => {
            const meta = truckOf(r.truck_id);
            return {
              truckId: r.truck_id,
              label: meta.label,
              color: meta.color,
              stops: (r.stops ?? []).map((s) => ({
                id: s.order_id,
                sequence: s.sequence,
                name: nameOf(s.order_id),
                address: clientById.get(s.order_id)?.principalAddress?.address ?? '',
                arrivalTime: s.arrival_time,
              })),
            };
          }),
      }))
      .filter((d) => d.trucks.length > 0);

    // No planificadas (definitivo): agrupar por motivo con etiqueta de UI.
    // Se guarda el cliente completo, no solo el nombre: la lista se muestra como
    // tarjetas (con dirección) porque en corrida real son decenas y un texto
    // corrido separado por puntos es ilegible.
    const byReason = new Map<string, UnplannedClient[]>();
    for (const u of res.unplanned ?? []) {
      const list = byReason.get(u.reason) ?? [];
      list.push({
        id: u.order_id,
        name: nameOf(u.order_id),
        address: clientById.get(u.order_id)?.principalAddress?.address ?? '',
        reason: u.reason,
      });
      byReason.set(u.reason, list);
    }
    this.unplannedGroups = [...byReason.entries()].map(([reason, items]) => ({
      reason,
      label: reasonLabel(reason),
      items,
    }));

    // Reprogramadas: un cliente por entrada, agrupadas por el día al que llegaron.
    // Se le pasan las no planificadas para que las que nunca entraron queden fuera
    // de este bloque — ya están explicadas arriba, con su motivo final.
    this.rolloverGroups = summarizeRollovers(res.days, res.unplanned, target);

    // Grupos del mapa por día: uno por camión (color del camión).
    this.dayGroups = this.multidayDays.map((day) => ({
      date: day.date,
      dayLabel: day.dayLabel,
      groups: day.trucks.map((truck) => ({
        groupName: `${truck.label} · ${day.dayLabel} ${day.date}`,
        groupColor: truck.color,
        markers: truck.stops
          .map((s) => {
            const geo = clientById.get(s.id)?.principalAddress?.geolocationPoint;
            if (!geo) return null;
            return {
              title: `${truck.label} — ${s.sequence}. ${s.name}`,
              subtitle: s.address,
              latitude: geo[0],
              longitude: geo[1],
              order: s.sequence,
            };
          })
          .filter((m): m is NonNullable<typeof m> => m !== null),
      })),
    }));
    // Por defecto, el mapa renderiza solo el primer día (rendimiento).
    this.selectedMapDate = this.dayGroups[0]?.date ?? '';
    this.refreshVisibleMarkers();
    this.computeDashboard(res);

    this.state = 'result';
    this.dropRejectedSubstitutions();
  }

  /** Métricas del plan multi-día para el dashboard. */
  private computeDashboard(res: PlanMultidayResponse): void {
    this.dash = computePlanDashboard(res, this.unplannedCount);
  }

  /** "Xh Ym" a partir de minutos. */
  formatDuration(min: number): string {
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    return h > 0 ? `${h} h ${m} min` : `${m} min`;
  }

  /** Día de la semana (1=Lun..7=Dom) de una fecha `YYYY-MM-DD`. */
  private weekdayCode(date: string): number {
    const js = new Date(`${date}T00:00:00`).getDay(); // 0=Dom..6=Sáb
    return js === 0 ? 7 : js;
  }

  private handleError(err: { status?: number; error?: { message?: string } }): void {
    this.state = 'error';
    const status = err?.status;
    const alternativeMessage =
      status === 503
        ? 'El motor de optimización no respondió. Intenta de nuevo en unos minutos.'
        : status === 422
          ? 'No se pudo optimizar (paradas sin calle alcanzable o entrada inválida).'
          : 'Ocurrió un error al optimizar.';
    // El porqué viene en `detail` (FastAPI), no en `message`: leer solo `message`
    // dejaba siempre el texto genérico y escondía qué campo no validó.
    this.errorDetail = apiErrorDetail(err);
    this.handlerMessage.error({
      message: this.errorDetail || undefined,
      alternativeMessage,
      statusCode: status,
    });
  }

  private fail(err: unknown, alternativeMessage: string): void {
    this.state = 'error';
    const e = err as { status?: number; error?: { message?: string } };
    this.errorDetail = apiErrorDetail(err);
    this.handlerMessage.error({
      message: this.errorDetail || undefined,
      alternativeMessage,
      statusCode: e?.status,
    });
  }

  retry(): void {
    this.state = this.clients.length ? 'ready' : 'loading';
    if (!this.clients.length) void this.loadSubRouteData();
  }

  close(): void {
    this.dialogRef.close();
  }
}
